import { ROLE_METADATA, hasAllPermissions } from "@thyroaid/shared";
import type { Permission, Role } from "@thyroaid/shared";
import type { Session } from "../session/session";

/**
 * Route access policy (WEB-004). UI guards only hide screens — the API enforces the same
 * permissions (ADR-0004). Keep this in sync by construction: it uses the shared role map.
 */
export interface AccessPolicy {
  /** Every listed permission is required. Empty list = any signed-in user. */
  readonly permissions: readonly Permission[];
}

export type AccessDecision =
  | { readonly outcome: "pending" }
  | { readonly outcome: "sign_in_required" }
  | { readonly outcome: "mfa_required" }
  | { readonly outcome: "forbidden"; readonly missing: readonly Permission[] }
  | { readonly outcome: "allowed" };

export function decideAccess(session: Session, policy: AccessPolicy): AccessDecision {
  switch (session.status) {
    case "loading":
      return { outcome: "pending" };
    case "anonymous":
      return { outcome: "sign_in_required" };
    case "authenticated": {
      const { roles, mfaVerified } = session.user;
      if (mfaRequiredFor(roles, mfaVerified)) {
        return { outcome: "mfa_required" };
      }
      if (hasAllPermissions(roles, policy.permissions)) return { outcome: "allowed" };
      const missing = policy.permissions.filter((p) => !hasAllPermissions(roles, [p]));
      return { outcome: "forbidden", missing };
    }
  }
}

/** A clinical role signed in without a second factor must verify first (AUTH-007). */
export function mfaRequiredFor(roles: readonly Role[], mfaVerified: boolean): boolean {
  return !mfaVerified && roles.some((r) => ROLE_METADATA[r].requiresMfa);
}
